import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl, isLegacyPath } from "@/lib/api";
import Layout from "@/components/Layout";
import CreatePostDialog from "@/components/CreatePostDialog";
import StatusBadge from "@/components/StatusBadge";
import AuthImage from "@/components/AuthImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Download, CalendarDays, List, ArrowUpRight, FileCheck, CheckCircle2, Clock, AlertTriangle, XCircle, ScrollText, ChevronDown, ChevronUp, CalendarClock, Instagram } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MONTHS_PT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const NETWORK_LABEL = { instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", tiktok: "TikTok" };
const TYPE_LABEL = { feed: "Feed", story: "Story", reels: "Reels" };
// Visual language per post_type in the calendar pills / dots
const TYPE_STYLE = {
  feed:   { bg: "#EAE3DB", text: "#3B2B23", dot: "#C07C5B", ring: "#C07C5B", icon: "▣", label: "Feed" },
  story:  { bg: "#F5D9C8", text: "#8A3B12", dot: "#E27B3F", ring: "#E27B3F", icon: "◐", label: "Story" },
  reels:  { bg: "#DDD1F5", text: "#4B2E83", dot: "#7B5CD6", ring: "#7B5CD6", icon: "▶",  label: "Reels" },
};

export default function ClientPlan() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = new Date();
  const [searchParams, setSearchParams] = useSearchParams();

  // Persist year/month/view in the URL so navigating away (e.g. to a post and
  // back) preserves the exact state the user was looking at.
  const year = parseInt(searchParams.get("year") || today.getFullYear(), 10);
  const month = parseInt(searchParams.get("month") || (today.getMonth() + 1), 10);
  const view = searchParams.get("view") || "calendar";

  const setView = (v) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("view", v); return n; }, { replace: true });

  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, changes_requested: 0, rejected: 0, scheduled: 0, published: 0 });
  const [client, setClient] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const isStaff = user?.role === "admin" || user?.role === "staff" || user?.role === "designer";
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [postsRes, statsRes, clientRes] = await Promise.all([
        api.get("/posts", { params: { client_id: clientId, year, month } }),
        api.get("/stats", { params: { client_id: clientId, year, month } }),
        api.get(`/clients/${clientId}`).catch(() => ({ data: null })),
      ]);
      setPosts(postsRes.data);
      setStats(statsRes.data);
      if (clientRes.data) {
        setClient(clientRes.data);
      } else if (isStaff) {
        const c = await api.get("/clients");
        const found = c.data.find((x) => x.id === clientId);
        setClient(found || { name: "Cliente", company: "" });
      } else {
        setClient({ name: user.name, company: user.company, logo_path: user.logo_path });
      }
    } catch {
      toast.error("Erro ao carregar plano");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, year, month]);

  const postsByDay = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      map[p.day] = map[p.day] || [];
      map[p.day].push(p);
    });
    return map;
  }, [posts]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Monday=0

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setSearchParams((p) => {
      const n = new URLSearchParams(p);
      n.set("year", String(y));
      n.set("month", String(m));
      return n;
    }, { replace: true });
    setSelectedDay(null);
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/plan/${clientId}/${year}/${month}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `plano-${year}-${String(month).padStart(2,"0")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao gerar PDF");
    }
  };

  const displayedPosts = selectedDay
    ? (postsByDay[selectedDay] || [])
    : [...posts].sort((a, b) => {
        const da = a.day || 0, db = b.day || 0;
        if (da !== db) return da - db;
        return (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
      });

  // Feed simulation: chronological order (day ascending), include only feed/reels
  const feedPosts = [...posts]
    .filter((p) => p.post_type !== "story")
    .sort((a, b) => {
      const da = a.day || 0, db = b.day || 0;
      if (da !== db) return da - db;
      return (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
    });

  return (
    <Layout
      subtitle={isStaff ? `Cliente · ${client?.company || ""}` : "O meu plano"}
      title={isStaff ? (client?.name || "") : "O meu mês"}
      actions={
        <div className="flex flex-wrap gap-2 sm:gap-3 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={downloadPdf}
            className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-10 sm:h-11 px-4 sm:px-5 text-[10px] sm:text-xs uppercase tracking-[0.2em] flex-1 md:flex-none"
            data-testid="download-pdf-button"
          >
            <Download className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Exportar </span>PDF
          </Button>
          {isStaff && (
            <CreatePostDialog
              clientId={clientId}
              year={year} month={month}
              defaultDay={selectedDay || 1}
              onCreated={load}
            />
          )}
        </div>
      }
    >
      {/* Contract terms (visible to both team and client) */}
      {client?.contract_terms ? (
        <ContractTermsCard terms={client.contract_terms} />
      ) : null}

      {/* Month navigator */}
      <div className="flex items-center justify-between mb-8 bg-white card-soft px-5 py-4">
        <button
          onClick={() => changeMonth(-1)}
          className="p-2 rounded-full hover:bg-[#EAE3DB] transition-colors"
          data-testid="prev-month-button"
        >
          <ChevronLeft className="w-5 h-5 text-[#3B2B23]" />
        </button>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C]">{year}</div>
          <div className="text-2xl sm:text-3xl text-[#1A1412]">{MONTHS_PT[month - 1]}</div>
        </div>
        <button
          onClick={() => changeMonth(1)}
          className="p-2 rounded-full hover:bg-[#EAE3DB] transition-colors"
          data-testid="next-month-button"
        >
          <ChevronRight className="w-5 h-5 text-[#3B2B23]" />
        </button>
      </div>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4 mb-8 sm:mb-10">
        {[
          { k: "total", label: "Total", icon: FileCheck, color: "#3B2B23" },
          { k: "approved", label: "Aprovados", icon: FileCheck, color: "#2C543B" },
          { k: "pending", label: "Pendentes", icon: Clock, color: "#805B10" },
          { k: "changes_requested", label: "Alterações", icon: AlertTriangle, color: "#C07C5B" },
          { k: "rejected", label: "Não aprovados", icon: XCircle, color: "#8A2A22" },
          { k: "scheduled", label: "Agendados", icon: CalendarClock, color: "#1E4A75" },
          { k: "published", label: "Publicados", icon: CheckCircle2, color: "#1E5A2C" },
        ].map((b) => {
          const Icon = b.icon;
          return (
            <Card key={b.k} className="card-soft p-4 sm:p-5" data-testid={`plan-stat-${b.k}`}>
              <div className="flex items-center justify-between text-[#5C4A43]">
                <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em]">{b.label}</span>
                <Icon className="w-4 h-4" style={{ color: b.color }} />
              </div>
              <div className="mt-2 text-2xl sm:text-3xl text-[#1A1412] font-bold">{stats[b.k] ?? 0}</div>
            </Card>
          );
        })}
      </section>

      {/* View tabs */}
      <Tabs value={view} onValueChange={(v) => { setView(v); setSelectedDay(null); }}>
        <TabsList className="bg-[#EAE3DB] rounded-full p-1 h-11">
          <TabsTrigger
            value="calendar"
            className="rounded-full data-[state=active]:bg-[#3B2B23] data-[state=active]:text-[#F9F6F0] px-5 text-sm"
            data-testid="tab-calendar"
          >
            <CalendarDays className="w-4 h-4 mr-2" /> Calendário
          </TabsTrigger>
          <TabsTrigger
            value="list"
            className="rounded-full data-[state=active]:bg-[#3B2B23] data-[state=active]:text-[#F9F6F0] px-5 text-sm"
            data-testid="tab-list"
          >
            <List className="w-4 h-4 mr-2" /> Lista
          </TabsTrigger>
          <TabsTrigger
            value="feed"
            className="rounded-full data-[state=active]:bg-[#3B2B23] data-[state=active]:text-[#F9F6F0] px-5 text-sm"
            data-testid="tab-feed"
          >
            <Instagram className="w-4 h-4 mr-2" /> Feed
          </TabsTrigger>
        </TabsList>

        {/* Calendar view */}
        <TabsContent value="calendar" className="mt-6">
          <Card className="card-soft p-3 sm:p-6">
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-3">
              {["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"].map((d) => (
                <div key={d} className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#8B5E3C] text-center py-1 sm:py-2">
                  <span className="hidden sm:inline">{d}</span>
                  <span className="sm:hidden">{d[0]}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {Array.from({ length: firstDayWeekday }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dayPosts = postsByDay[day] || [];
                const isSelected = selectedDay === day;
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDay(isSelected ? null : day)}
                    className={`min-h-[52px] sm:min-h-[88px] p-1.5 sm:p-2 text-left rounded-lg sm:rounded-xl border transition-all hover:-translate-y-0.5 ${
                      isSelected ? "bg-[#3B2B23] text-[#F9F6F0] border-[#3B2B23]" : "bg-white border-[#EAE3DB] hover:border-[#8B5E3C]"
                    }`}
                    data-testid={`calendar-day-${day}`}
                  >
                    <div className={`text-xs sm:text-sm font-medium ${isSelected ? "text-[#F9F6F0]" : "text-[#1A1412]"}`}>
                      {String(day).padStart(2, "0")}
                    </div>
                    {/* Mobile: small dots (colored by type) */}
                    <div className="mt-1 flex sm:hidden gap-0.5 flex-wrap">
                      {dayPosts.slice(0, 3).map((p) => {
                        const t = TYPE_STYLE[p.post_type] || TYPE_STYLE.feed;
                        return (
                          <span
                            key={p.id}
                            className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-[#F9F6F0]" : ""}`}
                            style={isSelected ? undefined : { background: t.dot }}
                          />
                        );
                      })}
                      {dayPosts.length > 3 && (
                        <span className={`text-[9px] leading-none ${isSelected ? "text-[#F9F6F0]/70" : "text-[#8B5E3C]"}`}>+{dayPosts.length - 3}</span>
                      )}
                    </div>
                    {/* Desktop: labels with type-coloured accent */}
                    <div className="mt-1 hidden sm:flex flex-col gap-1">
                      {dayPosts.slice(0, 2).map((p) => {
                        const nets = p.social_networks && p.social_networks.length ? p.social_networks : [p.social_network];
                        const netLabel = nets.filter(Boolean).map(n => NETWORK_LABEL[n] || n).join("/");
                        const t = TYPE_STYLE[p.post_type] || TYPE_STYLE.feed;
                        const isPublished = p.status === "published";
                        const isScheduled = p.status === "scheduled";
                        return (
                          <div
                            key={p.id}
                            data-testid={`calendar-pill-${p.id}`}
                            className={`text-[10px] truncate rounded-full px-2 py-0.5 flex items-center gap-1 border ${
                              isSelected
                                ? "bg-[#F9F6F0]/15 text-[#F9F6F0] border-[#F9F6F0]/25"
                                : ""
                            }`}
                            style={
                              isSelected
                                ? undefined
                                : isPublished
                                  ? { background: "#1E5A2C", color: "#F9F6F0", borderColor: "#1E5A2C" }
                                  : isScheduled
                                    ? { background: "#1E4A75", color: "#F9F6F0", borderColor: "#1E4A75" }
                                    : { background: t.bg, color: t.text, borderColor: t.ring + "40" }
                            }
                            title={`${t.label} · ${netLabel}${isPublished ? " · Já publicado" : isScheduled ? " · Agendado" : ""}`}
                          >
                            <span className="shrink-0 leading-none">{t.icon}</span>
                            <span className="truncate">{netLabel}</span>
                          </div>
                        );
                      })}
                      {dayPosts.length > 2 && (
                        <div className={`text-[10px] ${isSelected ? "text-[#F9F6F0]/70" : "text-[#8B5E3C]"}`}>
                          +{dayPosts.length - 2}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {selectedDay && (
            <div className="mt-8" data-testid="day-detail-section">
              <h3 className="text-2xl text-[#1A1412] mb-4">
                Dia {String(selectedDay).padStart(2, "0")} · {posts.filter(p => p.day === selectedDay).length} post(s)
              </h3>
              <PostsGrid posts={postsByDay[selectedDay] || []} navigate={navigate} />
            </div>
          )}
        </TabsContent>

        {/* List view */}
        <TabsContent value="list" className="mt-6">
          <PostsGrid posts={displayedPosts} navigate={navigate} />
        </TabsContent>

        {/* Instagram feed simulation */}
        <TabsContent value="feed" className="mt-6">
          <FeedSimulation posts={feedPosts} client={client} navigate={navigate} />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function PostsGrid({ posts, navigate }) {
  if (!posts.length) {
    return (
      <div className="card-soft p-12 text-center" data-testid="no-posts-empty">
        <div className="text-2xl text-[#1A1412]">Sem posts planeados</div>
        <div className="mt-2 text-[#5C4A43] text-sm">Ainda não tenho posts a planear para este período.</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {posts.map((p) => <PostCard key={p.id} post={p} navigate={navigate} />)}
    </div>
  );
}

function PostCard({ post, navigate }) {
  return (
    <Card
      onClick={() => navigate(`/post/${post.id}`)}
      className="card-soft overflow-hidden cursor-pointer group hover:-translate-y-0.5 transition-all"
      data-testid={`post-card-${post.id}`}
    >
      <div className="aspect-square bg-[#EAE3DB] relative overflow-hidden">
        {post.images?.[0] ? (
          post.images[0].media_type === "video" ? (
            <div className="w-full h-full bg-[#3B2B23] flex items-center justify-center">
              <span className="text-[#F9F6F0] text-4xl">▶</span>
            </div>
          ) : (
            <AuthImage path={post.images[0].path} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#8B5E3C] text-2xl font-bold">
            {(post.social_networks?.[0] || post.social_network) && (NETWORK_LABEL[post.social_networks?.[0] || post.social_network] || "?")[0]}
          </div>
        )}
        <div className="absolute top-3 left-3">
          <StatusBadge status={post.status} />
        </div>
        <ArrowUpRight className="w-4 h-4 text-white absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[10px] text-[#5C4A43] uppercase tracking-widest flex-wrap">
          <span className="font-bold text-[#3B2B23]">Dia {String(post.day).padStart(2, "0")}</span>
          <span>·</span>
          <span>{(post.social_networks && post.social_networks.length ? post.social_networks : [post.social_network]).filter(Boolean).map(n => NETWORK_LABEL[n] || n).join(" / ")}</span>
          <span>·</span>
          <span>{TYPE_LABEL[post.post_type] || post.post_type}</span>
        </div>
        <p className="notranslate mt-3 text-sm text-[#1A1412] line-clamp-3 leading-relaxed font-light" translate="no">
          {post.copy || <span className="text-[#8B5E3C] italic">Sem copy</span>}
        </p>
      </div>
    </Card>
  );
}

function ContractTermsCard({ terms }) {
  const [expanded, setExpanded] = useState(false);
  const preview = terms.length > 220 ? terms.slice(0, 220) + "…" : terms;
  return (
    <Card className="card-soft mb-6 border-l-4 border-l-[#C07C5B] border-[#EAE3DB]" data-testid="contract-terms-card">
      <div className="p-5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-start justify-between gap-3 text-left"
          data-testid="contract-terms-toggle"
          aria-expanded={expanded}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#F3E9DE] flex items-center justify-center shrink-0 mt-0.5">
              <ScrollText className="w-4 h-4 text-[#C07C5B]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C]">Condições contratuais</div>
              {!expanded && (
                <p className="notranslate mt-2 text-[13px] text-[#3B2B23] line-clamp-2 leading-relaxed" translate="no">
                  {preview}
                </p>
              )}
            </div>
          </div>
          <div className="shrink-0 pt-1">
            {expanded ? <ChevronUp className="w-4 h-4 text-[#8B5E3C]" /> : <ChevronDown className="w-4 h-4 text-[#8B5E3C]" />}
          </div>
        </button>
        {expanded && (
          <div className="notranslate mt-3 pt-3 border-t border-[#EAE3DB] whitespace-pre-wrap text-[13px] text-[#1A1412] leading-relaxed" translate="no" data-testid="contract-terms-full">
            {terms}
          </div>
        )}
      </div>
    </Card>
  );
}

function FeedMedia({ media, className = "" }) {
  const [videoBlob, setVideoBlob] = useState(null);
  const isVideo = media?.media_type === "video";
  const legacy = media && isLegacyPath(media.path);

  useEffect(() => {
    if (!isVideo || !legacy) { setVideoBlob(null); return undefined; }
    let cancelled = false;
    let url;
    (async () => {
      try {
        const resp = await api.get(`/files/${media.path}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(resp.data);
        setVideoBlob(url);
      } catch { setVideoBlob(null); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [isVideo, legacy, media?.path]);

  if (!media?.path) {
    return (
      <div className={`bg-[#F1EAE0] flex items-center justify-center text-[#8B5E3C] text-[10px] uppercase tracking-widest ${className}`}>
        Sem imagem
      </div>
    );
  }
  if (isVideo) {
    const src = legacy ? videoBlob : fileUrl(media.path);
    if (!src) return <div className={`bg-[#EAE3DB] animate-pulse ${className}`} />;
    return <video src={src} className={className} muted playsInline preload="metadata" />;
  }
  return <AuthImage path={media.path} className={className} />;
}

function FeedSimulation({ posts, client, navigate }) {
  if (!posts.length) {
    return (
      <div className="card-soft p-12 text-center" data-testid="feed-empty">
        <div className="text-2xl text-[#1A1412]">Feed vazio</div>
        <div className="mt-2 text-[#5C4A43] text-sm">Não há posts (feed/reels) neste mês para simular.</div>
      </div>
    );
  }
  const handle = (client?.name || "cliente").toLowerCase().replace(/\s+/g, "");
  return (
    <div className="max-w-sm mx-auto" data-testid="feed-simulation">
      {/* Mock profile header */}
      <div className="bg-white rounded-t-2xl px-4 py-5 border border-[#EAE3DB] border-b-0">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] p-[2px]">
            <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
              {client?.logo_path ? (
                <AuthImage path={client.logo_path} className="w-full h-full object-cover rounded-full" />
              ) : (
                <span className="text-[#3B2B23] font-bold text-xl">{(client?.name || "?")[0]?.toUpperCase()}</span>
              )}
            </div>
          </div>
          <div className="flex-1 grid grid-cols-3 gap-2 text-center text-[11px] text-[#3B2B23]">
            <div><div className="font-bold text-sm">{posts.length}</div><div className="text-[#8B5E3C]">Posts</div></div>
            <div><div className="font-bold text-sm">—</div><div className="text-[#8B5E3C]">Seguidores</div></div>
            <div><div className="font-bold text-sm">—</div><div className="text-[#8B5E3C]">A seguir</div></div>
          </div>
        </div>
        <div className="mt-3">
          <div className="font-bold text-sm text-[#1A1412]">{client?.name || "Cliente"}</div>
          <div className="text-[11px] text-[#8B5E3C]">@{handle}</div>
          {client?.company && <div className="text-[11px] text-[#3B2B23] mt-0.5">{client.company}</div>}
        </div>
      </div>
      {/* IG feed grid */}
      <div className="grid grid-cols-3 gap-[2px] bg-[#EAE3DB] border border-[#EAE3DB] rounded-b-2xl overflow-hidden">
        {posts.map((p) => {
          const isVideo = p.images?.[0]?.media_type === "video" || p.post_type === "reels";
          return (
            <button
              key={p.id}
              onClick={() => navigate(`/post/${p.id}`)}
              className="aspect-square bg-white relative group"
              title={`Dia ${String(p.day).padStart(2,"0")} · ${p.post_type}`}
              data-testid={`feed-cell-${p.id}`}
            >
              {p.images?.[0]?.path ? (
                <FeedMedia media={p.images[0]} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#F1EAE0] flex items-center justify-center text-[#8B5E3C] text-[10px] uppercase tracking-widest">
                  Sem imagem
                </div>
              )}
              {p.images?.length > 1 && (
                <div className="absolute top-1 right-1 text-white text-xs drop-shadow-lg">▤</div>
              )}
              {isVideo && (
                <div className="absolute top-1 right-1 text-white text-xs drop-shadow-lg">▶</div>
              )}
              <div className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/50 text-white font-bold">
                {String(p.day).padStart(2, "0")}
              </div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors" />
            </button>
          );
        })}
      </div>
      <div className="mt-3 text-center text-[10px] uppercase tracking-widest text-[#8B5E3C]">
        Simulação — posts ordenados por data (do início do mês para o fim)
      </div>
    </div>
  );
}


